<template>
  <CustomModal
    noPadding
    class="create-new-hotel-offer"
    :is-modal-open="isOpen"
    @toggleModal="closeModal"
    :overlay="'dark'"
    :with-header="true"
    :with-footer="true"
    :custom-classes="'tw:w-[100vw] tw:md:max-w-[768px] tw:md:w-[768px] tw:md:rounded-md tw:rounded-none'"
    dataTestId="create-new-hotel-offer-modal"
  >
    <template #modalHeader>
      <p v-if="type === 'create'" class="tw:text-xl tw:text-gray-700">
        {{ $t(createModalTitle) }}
      </p>
      <p v-if="type === 'edit'" class="tw:text-xl tw:text-gray-700">
        {{ $t('hotel_offer_step_update') }}
      </p>
      <p v-if="type === 'replace'" class="tw:text-xl tw:text-gray-700">
        {{ $t('hotel_offers_change_header') }}
      </p>
    </template>

    <div class="tw:w-full">
      <CreateOfferStepper
        :type="type"
        v-if="activeStepId !== 3"
        :steps="steps"
        :activeStep="activeStepId"
        @back="onBackButton"
      />

      <div class="tw:px-6" v-if="activeStepId === 0">
        <CreateNewOfferStep1
          :filteredTemplates="filteredTemplates"
          :filteringCategories="filteringCategories"
          :selectedFilter="selectedFilter"
          :offer="offer"
          :icons="icons"
          @selectOffer="selectOffer"
          @selectFilter="selectFilter"
          @openSuggestionsModal="openSuggestionsModal"
        />
      </div>
      <div class="tw:px-6" v-if="activeStepId === 1">
        <CreateNewOfferStep2
          :offer="offer"
          :icons="icons"
          :selectedDateOption="selectedDateOption"
          :dateOptions="dateOptions"
          :expirationDate="expirationDate"
          :offerTypes="offerTypes"
          :selectedOfferType="selectedOfferType"
          :discountValue="discountValue"
          :discountError="discountError"
          :dateError="dateError"
          :getMaxDate="getMaxDate"
          :getMinDate="getMinDate"
          @changeDateOption="changeDateOption"
          @changeExpirationDate="changeExpirationDate"
          @changeOfferType="changeOfferType"
          @setDiscountValue="setDiscountValue"
        />
      </div>
      <div class="tw:px-6" v-if="activeStepId === 2">
        <CreateNewOfferStep3
          :offer="offer"
          :type="type"
          :expirationDate="expirationDate"
          :responsibilityCheckbox="responsibilityCheckbox"
          :displayCheckbox="displayCheckbox"
          :selectedOfferType="selectedOfferType"
          :discountValue="discountValue"
          @toggleResponsibilityCheckbox="toggleResponsibilityCheckbox"
          @toggleDisplayCheckbox="toggleDisplayCheckbox"
        />
      </div>
      <div class="tw:px-6" v-if="activeStepId === 3 && type === 'replace'">
        <CreateNewOfferStep4
          :activeOffer="activeOffer"
          :hasCancellationDate="hasCancellationDate"
          :newOffer="offer"
          :selectedOfferType="selectedOfferType"
          :discountValue="discountValue"
          :selectedDateOption="selectedDateOption"
          :expirationDate="expirationDate"
        />
      </div>
    </div>

    <template #modalFooter>
      <div class="tw:flex tw:w-full tw:justify-end tw:gap-x-4">
        <GhostButton
          @click="onBackButton(null)"
          v-show="showBackButton"
          dataTestId="create-new-hotel-offer-modal-back-button"
          >{{ $t('hotel_offers_cta_back') }}</GhostButton
        >
        <MainButton :disabled="nextDisabled" @click="nextAction" dataTestId="create-new-hotel-offer-modal-next-button">
          <span v-if="isCreatingOfferLoading || isEditingOfferLoading"><SmallDotLoader color="#fff" /></span>
          <span v-else>{{ $t(nextActionBtnLabel) }}</span>
        </MainButton>
      </div>
    </template>
  </CustomModal>
</template>
<script setup>
import { ref, computed, watch } from 'vue'

import CustomModal from '@/components/base/CustomModal.vue'
import CreateOfferStepper from '@/components/hotelOffers/createOffer/CreateOfferStepper.vue'
import CreateNewOfferStep2 from '@/components/hotelOffers/createOffer/CreateNewOfferStep2.vue'
import CreateNewOfferStep1 from '@/components/hotelOffers/createOffer/CreateNewOfferStep1.vue'
import CreateNewOfferStep3 from '@/components/hotelOffers/createOffer/CreateNewOfferStep3.vue'
import CreateNewOfferStep4 from '@/components/hotelOffers/createOffer/CreateNewOfferStep4.vue'

import MainButton from '@/components/base/buttons/MainButton.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'
import useToastNotifications from '@/components/notifications/useToastNotifications'

import appAnalytics from '@/utils/tracking'

import { useStudioPlusCampaign } from '@/components/payment/queries'
import { useCreateOffer, useEditOffer } from '@/components/hotelOffers/queries'
import { selectedAccommodationId } from '@/layouts/queries'
import { isDateValid } from '@/utils/hotelOffersUtils'
import { useRoute } from 'vue-router'

const emit = defineEmits(['closeModal', 'openSuggestionsModal', 'selectOffer'])
const props = defineProps({
  isOpen: Boolean,
  hasActiveOffer: Boolean,
  filteringCategories: Array,
  templates: Array,
  offer: Object,
  icons: Object,
  type: String,
  activeOffer: Object,
  source: String,
  hasCancellationDate: Boolean,
})

const route = useRoute()
const { displayNotification } = useToastNotifications()
const { isCancelledSubscription, studioPlusCampaign } = useStudioPlusCampaign(selectedAccommodationId)
const { createOffer, isCreatingOfferLoading } = useCreateOffer()
const { editOffer, isEditingOfferLoading } = useEditOffer()

const closeModal = () => {
  activeStepId.value = 0
  expirationDate.value = null
  discountValue.value = null
  selectedDateOption.value = 0
  selectedOfferType.value = 0
  displayCheckbox.value = false
  responsibilityCheckbox.value = false
  selectedFilter.value = 'all'
  emit('closeModal', false)
}
const openSuggestionsModal = () => {
  emit('closeModal', true)
  emit('openSuggestionsModal', 'popup')
}

const steps = [
  { id: 0, name: 'hotel_offers_stepper_1' },
  { id: 1, name: 'hotel_offers_stepper_2' },
  { id: 2, name: 'hotel_offers_stepper_3' },
]
const discountValue = ref(null)
const selectedDateOption = ref(0)
const dateOptions = [
  { id: 0, label: 'hotel_offers_no_expiration' },
  { id: 1, label: 'hotel_offers_expiration_date' },
]
const offerTypes = [
  { id: 0, label: 'hotel_offers_tab_free' },
  { id: 1, label: 'hotel_offers_tab_discount' },
]

const getMaxDate = computed(() => {
  if (isCancelledSubscription.value) {
    let date = new Date(studioPlusCampaign.value.endDate.replace('@', 'T'))
    return new Date(date).toISOString().split('T')[0]
  } else {
    let date = new Date('2038-01-19 03:14:07')
    return new Date(date).toISOString().split('T')[0]
  }
})
const getMinDate = computed(() => {
  let date = new Date()

  date.setDate(date.getDate() + 1)
  return date.toISOString().split('T')[0]
})

const responsibilityCheckbox = ref(false)
const displayCheckbox = ref(false)
const selectedOfferType = ref(0)
const expirationDate = ref(null)
const activeStepId = ref(props.type === 'edit' ? 1 : 0)
const selectOffer = offerName => {
  emit('selectOffer', offerName)
}

watch(
  () => props.type,
  newValue => {
    if (newValue === 'edit') {
      activeStepId.value = 1
      expirationDate.value = props.activeOffer.expirationDate?.split('@')[0]
      selectedDateOption.value = props.hasCancellationDate ? 1 : 0
      selectedOfferType.value = props.activeOffer?.benefitValue === null ? 0 : 1
      discountValue.value = props.activeOffer?.benefitValue
    } else {
      activeStepId.value = props.offer === null || props.source === 'suggestions' ? 0 : 1
    }
  },
  { immediate: true }
)

const createModalTitle = computed(() => {
  if (activeStepId.value === 0) return 'hotel_offers_step_offer'
  else if (activeStepId.value === 1) return 'hotel_offers_step_customize'
  else if (activeStepId.value === 2) return 'hotel_offers_step_review'
  return ''
})
const nextActionBtnLabel = computed(() => {
  if (activeStepId.value === 2 && props.type === 'create') return 'hotel_offers_cta_post_offer'
  else if (activeStepId.value === 2 && props.type === 'edit') return 'hotel_offers_cta_update_offer'
  else if (activeStepId.value === 3 && props.type === 'replace') return 'hotel_offers_cta_new'
  else return 'hotel_offers_cta_next'
})

const showBackButton = computed(() => {
  if (activeStepId.value === 1 && props.type === 'edit') return false
  else if (activeStepId.value === 0) return false
  else return true
})
const dateError = computed(() => {
  if (expirationDate.value === undefined) return ''
  if (!isDateValid(expirationDate.value)) return 'hotel_offers_expiration_invalid'
  if (expirationDate.value && expirationDate.value < getMinDate.value) return 'hotel_offers_expiration_error'
  if (expirationDate.value && expirationDate.value > getMaxDate.value) return 'hotel_offers_expiration_invalid'
  return ''
})
const discountError = computed(() => {
  if (discountValue.value === null) return ''
  else if (discountValue.value > 99 || discountValue.value < 5 || !Number.isInteger(Number(discountValue.value)))
    return 'hotel_offers_discount_error'
  else return ''
})

const nextDisabled = computed(() => {
  const activeOfferExpirationDate = props.activeOffer?.expirationDate
  if (activeStepId.value === 0) {
    return props.offer === null
  } else if (
    activeStepId.value === 1 &&
    selectedDateOption.value === 1 &&
    (expirationDate.value === undefined || expirationDate.value === null || dateError.value.length > 0)
  ) {
    return true
  }
  if (
    activeStepId.value === 1 &&
    props.offer?.type === 'DISCOUNT' &&
    selectedOfferType.value === 1 &&
    (discountValue.value < 5 ||
      discountValue.value > 99 ||
      discountError.value.length > 0 ||
      !Number.isInteger(Number(discountValue.value)))
  ) {
    return true
  }
  if (
    props.type === 'edit' &&
    activeStepId.value === 1 &&
    props.offer.name === props.activeOffer.template &&
    ((props.activeOffer.benefitValue === discountValue.value && selectedOfferType.value === 1) ||
      (props.activeOffer.benefitValue === null && selectedOfferType.value === 0)) &&
    activeOfferExpirationDate?.split('@')[0] === expirationDate.value
  ) {
    return true
  }
  if (activeStepId.value === 2 && (!responsibilityCheckbox.value || !displayCheckbox.value)) {
    return true
  }
  return false
})

const toggleResponsibilityCheckbox = () => {
  responsibilityCheckbox.value = !responsibilityCheckbox.value
}
const toggleDisplayCheckbox = () => {
  displayCheckbox.value = !displayCheckbox.value
}
const changeExpirationDate = date => {
  expirationDate.value = date
}
const changeDateOption = dateOptionId => {
  selectedDateOption.value = dateOptionId
  if (dateOptionId === 0) {
    expirationDate.value = null
  } else if (props.type === 'edit' && dateOptionId === 1) {
    expirationDate.value = props.activeOffer.expirationDate?.split('@')[0]
  }
}
const changeOfferType = offerId => {
  selectedOfferType.value = offerId
}
const setDiscountValue = value => {
  discountValue.value = value
}

const nextAction = () => {
  if (activeStepId.value === 0) {
    activeStepId.value = 1
  } else if (activeStepId.value === 1) {
    activeStepId.value = 2
  } else if (activeStepId.value === 2 && props.type === 'create') {
    publishOffer()
  } else if (activeStepId.value === 2 && props.type === 'edit') {
    updateOffer()
  } else if (activeStepId.value === 2 && props.type === 'replace') {
    activeStepId.value = 3
  } else if (activeStepId.value === 3 && props.type === 'replace') {
    publishOffer()
  }
}

const onBackButton = stepId => {
  if (stepId === null) {
    if (activeStepId.value === 1) {
      activeStepId.value = 0
    } else if (activeStepId.value === 2) {
      activeStepId.value = 1
    } else if (activeStepId.value === 3 && props.type === 'replace') {
      activeStepId.value = 2
    }
  } else {
    activeStepId.value = stepId
  }
}

const cancelledExpirationDate = computed(() => {
  let date = new Date(studioPlusCampaign.value.endDate.replace('@', 'T'))
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const cancellationDate = `${year}-${month}-${day}`
  if (selectedDateOption.value === 1) {
    if (expirationDate.value < cancellationDate) {
      return expirationDate.value
    }
  }

  return cancellationDate
})

const updateOffer = () => {
  editOffer(
    {
      accommodationId: selectedAccommodationId.value,
      body: {
        expirationDate: isCancelledSubscription.value ? cancelledExpirationDate.value : expirationDate.value,
        benefitValue: selectedOfferType.value === 1 ? discountValue.value : null,
      },
    },
    {
      onSuccess: () => {
        closeModal()
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        })
        displayNotification({
          message: 'hotel_offers_success_posted',
          isTranslationKey: true,
          type: 'success',
        })
      },
      onError: () => {
        displayNotification({
          message: 'something_went_wrong',
          isTranslationKey: true,
          type: 'error',
        })
      },
    }
  )
}

const publishOffer = () => {
  createOffer(
    {
      accommodationId: selectedAccommodationId.value,
      replaceOldOffer: props.hasActiveOffer,
      body: {
        templateName: props.offer.name,
        expirationDate: isCancelledSubscription.value ? cancelledExpirationDate.value : expirationDate.value,
        benefitValue: selectedOfferType.value === 1 ? discountValue.value : null,
      },
    },
    {
      onSuccess: () => {
        closeModal()
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        })
        displayNotification({
          message: 'hotel_offers_success_posted',
          isTranslationKey: true,
          type: 'success',
        })

        if (route.query?.from_insights === 'true') {
          appAnalytics.track(appAnalytics.events.SP_PLUS_FEATURE_ACTIVATED_FROM_INSIGHTS, {
            item_id: selectedAccommodationId.value,
            benefit_type: 'hotel_offer_badge',
            benefit_status: 'active',
          })
        }
      },
      onError: () => {
        displayNotification({
          message: 'something_went_wrong',
          isTranslationKey: true,
          type: 'error',
        })
      },
    }
  )
}

const selectedFilter = ref('all')
const selectFilter = selectedValue => {
  selectedFilter.value = selectedValue
}
const filteredTemplates = ref([])

const applyFilter = (newTemplates, filter) => {
  if (filter === 'all') {
    filteredTemplates.value = newTemplates
  } else {
    filteredTemplates.value = newTemplates.filter(item => item.category === filter)
  }
}

watch(
  [() => props.templates, selectedFilter],
  ([newTemplates, newFilter]) => {
    applyFilter(newTemplates, newFilter)
  },
  { immediate: true }
)
</script>
